import { useState } from 'react';
import { Calendar, Copy, Check, RefreshCw, Smartphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth';
import { userApi } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';

export function CalendarSubscriptionSection() {
  const { user, updateUser } = useAuthStore();
  const { toast } = useToast();

  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const regenerateToken = async () => {
    setIsRegenerating(true);
    try {
      const response = await userApi.regenerateIcsToken();
      const { icsToken, icsUrl } = response.data.data;
      updateUser({ icsToken, icsUrl });
      toast({
        title: '重新生成成功',
        description: '您的日历订阅链接已更新，旧链接将失效',
        variant: 'success',
      });
    } catch {
      toast({
        title: '重新生成失败',
        description: '请稍后重试',
        variant: 'destructive',
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const copyIcsUrl = async () => {
    if (!user?.icsUrl) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(user.icsUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = user.icsUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!success) throw new Error('execCommand copy failed');
      }

      setCopied(true);
      toast({
        title: '已复制',
        description: '订阅链接已复制到剪贴板',
        variant: 'success',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: '复制失败',
        description: '请手动复制订阅链接',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            iOS 日历订阅
          </CardTitle>
          <CardDescription>将航班日程同步到您的 iPhone 日历</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="font-medium mb-2">订阅链接</p>
            <div className="flex gap-2">
              <div className="flex-1 px-4 py-3 bg-gray-100 rounded-lg text-sm text-gray-600 font-mono break-all">
                {user?.icsUrl || '请先生成订阅链接'}
              </div>
              <Button
                variant="outline"
                onClick={copyIcsUrl}
                disabled={!user?.icsUrl}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
            <div>
              <p className="font-medium text-yellow-800">重新生成 Token</p>
              <p className="text-sm text-yellow-600">
                重新生成后，旧的订阅链接将失效
              </p>
            </div>
            <Button
              variant="outline"
              onClick={regenerateToken}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              重新生成
            </Button>
          </div>

          <div className="border-t pt-6">
            <p className="font-medium mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              iPhone 设置教程
            </p>
            <ol className="space-y-3 text-sm text-gray-600">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                  1
                </span>
                <span>打开 iPhone「设置」→「日历」→「账户」</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                  2
                </span>
                <span>点击「添加账户」→「其他」→「添加已订阅的日历」</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                  3
                </span>
                <span>粘贴上方的订阅链接，点击「下一步」</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                  4
                </span>
                <span>等待验证完成后，点击「存储」即可</span>
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
